import { io, type Socket } from "socket.io-client";

export type Role = "cam" | "out";

// Проект раньше назывался иначе: переносим сохранённые настройки телефона (кодек, захват, приоритет),
// чтобы после обновления они не сбросились.
try {
  for (const store of [localStorage, sessionStorage]) {
    for (const key of Object.keys(store)) {
      if (!key.startsWith("pcv-")) continue;
      const next = `aircut-${key.slice(4)}`;
      if (store.getItem(next) === null) store.setItem(next, store.getItem(key)!);
      store.removeItem(key);
    }
  }
} catch {}

export interface SlotParams {
  event: string;
  slot: number;
  key: string;
  query: URLSearchParams;
  // Сигналинг не на этом сервере, а на сайте (студия: камеры подключены к сайту).
  base?: string;
}

export function slotParams(): SlotParams | null {
  const m = location.pathname.match(/^\/(?:cam|out)\/([A-Za-z0-9_-]+)\/(\d+)\/?$/);
  const query = new URLSearchParams(location.search);
  const key = query.get("k");
  if (!m || !key) return null;
  const sig = query.get("sig") ?? "";
  return { event: m[1], slot: Number(m[2]), key, query, base: /^https?:\/\/[^/]+$/.test(sig) ? sig : undefined };
}

export function clientId(role: Role): string {
  const storageKey = `aircut-cid:${role}:${location.pathname}`;
  try {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) return saved;
  } catch {}
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  const cid = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  try {
    sessionStorage.setItem(storageKey, cid);
  } catch {}
  return cid;
}

export function connectSignaling(role: Role, p: SlotParams, cid: string, extra: Record<string, unknown> = {}): Socket {
  const opts = {
    auth: { role, event: p.event, slot: p.slot, key: p.key, cid, ...extra },
    transports: ["websocket"],
    reconnectionDelay: 500,
    reconnectionDelayMax: 3000,
  };
  return p.base ? io(p.base, opts) : io(opts);
}

export async function fetchIce(role: Role, p: SlotParams, relayOnly = false): Promise<RTCConfiguration> {
  const config = await loadIce(role, p);
  if (relayOnly) config.iceTransportPolicy = "relay";
  return config;
}

async function loadIce(role: Role, p: SlotParams): Promise<RTCConfiguration> {
  const q = new URLSearchParams({ e: p.event, s: String(p.slot), r: role, k: p.key });
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(`/api/ice?${q}`, { cache: "no-store" });
      if (res.status === 401) throw new Error("bad-link");
      const { iceServers } = await res.json();
      return { iceServers, bundlePolicy: "max-bundle", rtcpMuxPolicy: "require" };
    } catch (err) {
      if ((err as Error).message === "bad-link" || attempt >= 4) {
        return { iceServers: [{ urls: "stun:stun.l.google.com:19302" }], bundlePolicy: "max-bundle" };
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
  }
}

const CODEC_MIME: Record<string, string> = {
  h264: "video/H264",
  vp8: "video/VP8",
  vp9: "video/VP9",
  av1: "video/AV1",
};

export function preferVideoCodec(transceiver: RTCRtpTransceiver, codec: string) {
  const caps = RTCRtpSender.getCapabilities?.("video");
  if (!caps || !transceiver.setCodecPreferences) return;
  const order = [CODEC_MIME[codec] ?? "video/H264", "video/H264", "video/VP8", "video/VP9"];
  const rank = (c: RTCRtpCodec) => {
    const i = order.indexOf(c.mimeType);
    const h264Bonus = c.mimeType === "video/H264" && /packetization-mode=1/.test(c.sdpFmtpLine ?? "") ? -0.5 : 0;
    return (i === -1 ? order.length : i) + h264Bonus;
  };
  const sorted = [...caps.codecs].sort((a, b) => rank(a) - rank(b));
  try {
    transceiver.setCodecPreferences(sorted);
  } catch {}
}

function videoPayloads(sdp: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of sdp.matchAll(/^a=rtpmap:(\d+) ([A-Za-z0-9]+)\/90000/gm)) map.set(m[1], m[2].toUpperCase());
  return map;
}

export function tuneRemoteSdp(sdp: string, maxKbps: number): string {
  const lines = sdp.split("\r\n");
  const payloads = videoPayloads(sdp);
  const start = Math.round(maxKbps * 0.6);
  const videoParams = `x-google-start-bitrate=${start};x-google-max-bitrate=${maxKbps}`;
  const withFmtp = new Set<string>();
  let opusPt: string | null = null;
  for (const line of lines) {
    const m = line.match(/^a=rtpmap:(\d+) opus\/48000/i);
    if (m) opusPt = m[1];
  }

  const out = lines.map((line) => {
    const m = line.match(/^a=fmtp:(\d+) (.*)$/);
    if (!m) return line;
    const [, pt, params] = m;
    const name = payloads.get(pt);
    if (name && ["VP8", "VP9", "H264", "AV1"].includes(name)) {
      withFmtp.add(pt);
      return `a=fmtp:${pt} ${params};${videoParams}`;
    }
    if (pt === opusPt) {
      const clean = params.split(";").filter((p) => !/^(stereo|sprop-stereo|maxaveragebitrate|useinbandfec)=/.test(p));
      return `a=fmtp:${pt} ${[...clean, "stereo=1", "sprop-stereo=1", "maxaveragebitrate=192000", "useinbandfec=1"].join(";")}`;
    }
    return line;
  });

  const result: string[] = [];
  for (const line of out) {
    result.push(line);
    const m = line.match(/^a=rtpmap:(\d+) ([A-Za-z0-9]+)\/90000/);
    if (m && ["VP8", "VP9", "H264", "AV1"].includes(m[2].toUpperCase()) && !withFmtp.has(m[1])) {
      result.push(`a=fmtp:${m[1]} ${videoParams}`);
    }
  }
  return result.join("\r\n");
}

export function $(selector: string): HTMLElement {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) throw new Error(`missing ${selector}`);
  return el;
}

export interface Route {
  route: string;
  rtt: number | null;
  bwe: number | null;
}

export function routeOf(report: RTCStatsReport): Route {
  let pair: { localCandidateId?: string; remoteCandidateId?: string; currentRoundTripTime?: number; availableOutgoingBitrate?: number } | undefined;
  report.forEach((s) => {
    if (s.type === "transport" && s.selectedCandidatePairId) pair = report.get(s.selectedCandidatePairId);
  });
  if (!pair) {
    report.forEach((s) => {
      if (!pair && s.type === "candidate-pair" && s.nominated && s.state === "succeeded") pair = s;
    });
  }
  if (!pair) return { route: "", rtt: null, bwe: null };
  const local = pair.localCandidateId ? report.get(pair.localCandidateId) : undefined;
  const remote = pair.remoteCandidateId ? report.get(pair.remoteCandidateId) : undefined;
  const rtt = pair.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : null;
  const bwe = pair.availableOutgoingBitrate != null ? Math.round(pair.availableOutgoingBitrate / 1000) : null;
  if (!local || !remote) return { route: "", rtt, bwe };
  if (local.candidateType === "relay") return { route: `relay-${local.relayProtocol ?? "udp"}`, rtt, bwe };
  if (remote.candidateType === "relay") return { route: "relay", rtt, bwe };
  return { route: "direct", rtt, bwe };
}

export function routeLabel(route: string): string {
  if (route === "direct") return "напрямую";
  if (route.startsWith("relay")) {
    const proto = route.split("-")[1];
    return proto ? `через TURN (${proto.toUpperCase()})` : "через TURN";
  }
  return "";
}
